import { forwardRef, type ReactNode } from 'react'

import { motion, type HTMLMotionProps } from 'motion/react'

import { cn } from '../../../utils/cn'

import { hasOpenPopup, useButtonMotion } from './button-motion'
import { ButtonRipple } from './button-ripple'
import {
  ButtonTrailingIcon,
  type ButtonTrailingIconOptions
} from './button-trailing-icon'
import {
  DEFAULT_SIZE,
  DEFAULT_VARIANT,
  resolveSlots,
  type ButtonSize,
  type ButtonVariantOptions
} from './button-variants'

export type ButtonLinkProps = Omit<HTMLMotionProps<'a'>, 'children'> &
  ButtonVariantOptions &
  ButtonTrailingIconOptions & {
    iconOnly?: boolean
    animated?: boolean
    /** Ømni has three sizes; the deprecated `xs`/`icon` are button-only. */
    size?: ButtonSize
    /**
     * A motion-enabled anchor, prepared once by the app's router adapter.
     * Defaults to motion.a; acqua does not wrap or cache router components.
     */
    component?: typeof motion.a
    children?: ReactNode
  }

/**
 * The Ømni Button for something that navigates.
 *
 * `Button` is always a `<button>`, and an anchor's semantics — cmd-click,
 * open in a new tab, being announced as a link — are not something it can
 * fake. This is the same control on an anchor: the same slots and the same
 * `useButtonMotion`, on a `motion.a` or the app's prepared router anchor,
 * so a navigating pill gets the lift, press and ripple its
 * button siblings have. `buttonClassName` stays for the cases even this cannot
 * wrap.
 *
 * What it deliberately does not carry from Button: `disabled` (an anchor has
 * no disabled state — render text instead), `loading` (a link does not wait)
 * and the menu indicators `caret` and `caretWithDivider`.
 */
export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  (
    {
      variant = DEFAULT_VARIANT,
      size = DEFAULT_SIZE,
      iconOnly = false,
      animated = true,
      trailingIcon,
      trailingIconMotion,
      component: MotionAnchor = motion.a,
      className,
      children,
      onPointerDown,
      ...props
    },
    ref
  ) => {
    const {
      shouldAnimate,
      ripple,
      initialRipples,
      handlePointerDown,
      motionProps
    } = useButtonMotion<HTMLAnchorElement>({
      animated,
      lift: !hasOpenPopup(props as Record<string, unknown>),
      onPointerDown
    })

    const slots = resolveSlots(variant, size, iconOnly)
    return (
      <MotionAnchor
        ref={ref}
        data-slot='button-link'
        data-variant={variant}
        data-size={size}
        data-icon-only={iconOnly || undefined}
        data-motion-enabled={shouldAnimate || undefined}
        className={cn(slots.root, className)}
        onPointerDown={handlePointerDown}
        {...motionProps}
        {...props}
      >
        {initialRipples && (
          <ButtonRipple
            ref={ripple}
            initialRipples={initialRipples}
            enabled={shouldAnimate}
          />
        )}
        <span data-slot='button-content' className={slots.content}>
          {children}
          {trailingIcon && !iconOnly && (
            <ButtonTrailingIcon
              name={trailingIcon}
              motion={trailingIconMotion}
            />
          )}
        </span>
      </MotionAnchor>
    )
  }
)
ButtonLink.displayName = 'ButtonLink'
